import { Virtuoso } from 'react-virtuoso';
import { memo, useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Menu, Select, Button, Checkbox, MenuItem, TextField, Typography, FormControlLabel } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

import type { ChipFieldSpec } from 'src/components/saved-views/chip-adapter';

import { varAlpha } from 'src/theme/styles';
import { AuthorizationLevel } from 'src/types/authorization';

import { Iconify } from '../iconify';

import {
  addFilterSelections,
  areAllFilterKeysSelected,
  areSomeFilterKeysSelected,
  isFilterKeySelected,
  removeFilterSelections,
  toggleFilterSelection,
  type FilterDropdownKey,
} from './filter-dropdown-selection';

export type { FilterDropdownKey };

export interface FilterOptionEntry {
  label: string;
  /** When set, this toolbar field is included in saved-view persistence. */
  savedView?: ChipFieldSpec;
  children?: FilterOptionChild[];
  onClick?: () => void;
  loadMore?: () => void;
  onSearch?: (search: string | null) => void;
  maxWidth?: number;
  /** Per-competency accreditation body options shown next to the level dropdown. */
  accreditationBodyOptions?: FilterOptionChild[];
  accreditationBodyPlaceholder?: string;
}

export interface FilterOption {
  [key: string]: FilterOptionEntry;
}

interface FilterOptionChild {
  key: string | number;
  label: string;
  onClick?: () => void;
}

interface SelectedValues {
  [key: string]: FilterDropdownKey[];
}
interface SelectedLevelValues {
  [field: string]: Record<string, AuthorizationLevel>;
}
interface SelectedAccreditationBodyValues {
  [field: string]: Record<string, number>;
}
export interface SelectedOptions {
  field: string;
  key: FilterDropdownKey;
  label: string;
  levelValue?: AuthorizationLevel;
  accreditationBodyValue?: number;
  accreditationBodyLabel?: string;
}
interface Props {
  buttonLabel: string;
  cancelLabel: string;
  applyLabel: string;
  options: FilterOption;
  selectedOptions: SelectedOptions[] | null;
  onOptionsChanged: (options: SelectedOptions[], touchedFields?: string[]) => void;
  buttonDimensions?: {
    height?: number;
    width?: number;
  };
  /** When true, show Select all / Clear all for the active multi-select filter field. */
  enableFieldBulkActions?: boolean;
}

type SelectedFilterOption = FilterOptionChild & { isSelected: boolean };

const COMPETENCY_FILTER_FIELD = 'competenciesIds';

const competencyLevelOptions = [
  AuthorizationLevel.LEAD,
  AuthorizationLevel.COMPETENT,
  AuthorizationLevel.EXPERT,
];

const insetMenuItemSx = {
  mx: 1,
  my: 0.5,
  borderRadius: 1,
  transition: 'none !important',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:active': { backgroundColor: 'transparent' },
  '&:focus-visible': { backgroundColor: 'transparent' },
  '& .MuiTouchRipple-root': { display: 'none !important' },
};

/** Prevent parent filter Menu from also handling arrow keys when a nested Select menu is open. */
const nestedSelectMenuProps = {
  onKeyDown: (event: React.KeyboardEvent) => {
    event.stopPropagation();
  },
};

// Memoized menu item for better performance
const FilterMenuItem = memo(
  ({
    option,
    isSelected,
    onClick,
  }: {
    option: FilterOptionChild;
    isSelected: boolean;
    onClick: () => void;
  }) => (
    <MenuItem
      onClick={onClick}
      title={option.label}
      disableRipple
      sx={{
        ...insetMenuItemSx,
      }}
    >
      <Checkbox
        checked={isSelected}
        disableRipple
        disableFocusRipple
        sx={{
          transition: 'none !important',
          '& .MuiSvgIcon-root': { transition: 'none !important' },
          '&.Mui-checked': { color: '#00D5D4' },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {option.label}
      </Typography>
    </MenuItem>
  )
);

const SelectedCompetencyDetailRow = memo(
  ({
    label,
    levelValue,
    onLevelChange,
    accreditationBodyValue,
    onAccreditationBodyChange,
    accreditationBodyOptions,
    accreditationBodyPlaceholder,
  }: {
    label: string;
    levelValue?: AuthorizationLevel;
    onLevelChange: (level: AuthorizationLevel) => void;
    accreditationBodyValue?: number;
    onAccreditationBodyChange?: (accreditationBodyId: number) => void;
    accreditationBodyOptions?: FilterOptionChild[];
    accreditationBodyPlaceholder?: string;
  }) => {
    const { t } = useTranslation('organization');

    return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1,
        py: 0.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
        title={label}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Select
          size="small"
          value={levelValue ?? ''}
          displayEmpty
          onChange={(event: SelectChangeEvent) => {
            onLevelChange(event.target.value as AuthorizationLevel);
          }}
          MenuProps={nestedSelectMenuProps}
          sx={{ minWidth: 124, height: 30 }}
        >
          <MenuItem value="" disabled>
            {t('project-roles.form.level')}
          </MenuItem>
          {competencyLevelOptions.map((level) => (
            <MenuItem key={level} value={level}>
              {t(`project-roles.levels.${level}`)}
            </MenuItem>
          ))}
        </Select>
        {accreditationBodyOptions?.length ? (
          <Select
            size="small"
            value={accreditationBodyValue != null ? String(accreditationBodyValue) : ''}
            displayEmpty
            onChange={(event: SelectChangeEvent) => {
              onAccreditationBodyChange?.(Number(event.target.value));
            }}
            MenuProps={nestedSelectMenuProps}
            sx={{ minWidth: 124, height: 30, maxWidth: 160 }}
          >
            <MenuItem value="" disabled>
              {accreditationBodyPlaceholder ?? t('project-roles.form.accreditationBody', { defaultValue: 'Accreditation body' })}
            </MenuItem>
            {accreditationBodyOptions.map((option) => (
              <MenuItem key={option.key} value={String(option.key)}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Box>
    </Box>
    );
  }
);

// Main component with performance optimizations
export const FilterDropdown = ({
  buttonLabel,
  cancelLabel,
  applyLabel,
  options,
  selectedOptions,
  onOptionsChanged,
  buttonDimensions = {
    height: 30,
  },
  enableFieldBulkActions = false,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState<null | HTMLElement>(null);
  const [activeOption, setActiveOption] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<SelectedValues>({
    ...(selectedOptions?.reduce(
      (acc: SelectedValues, opt) => ({ ...acc, [opt.field]: [...(acc[opt.field] ?? []), opt.key] }),
      {}
    ) || {})
  });
  const [selectedLevelValues, setSelectedLevelValues] = useState<SelectedLevelValues>({
    ...(selectedOptions?.reduce((acc: SelectedLevelValues, opt) => {
      if (!opt.levelValue) return acc;
      return {
        ...acc,
        [opt.field]: {
          ...(acc[opt.field] ?? {}),
          [String(opt.key)]: opt.levelValue,
        },
      };
    }, {}) || {}),
  });
  const [selectedAccreditationBodyValues, setSelectedAccreditationBodyValues] =
    useState<SelectedAccreditationBodyValues>({
      ...(selectedOptions?.reduce((acc: SelectedAccreditationBodyValues, opt) => {
        if (opt.accreditationBodyValue == null) return acc;
        return {
          ...acc,
          [opt.field]: {
            ...(acc[opt.field] ?? {}),
            [String(opt.key)]: opt.accreditationBodyValue,
          },
        };
      }, {}) || {}),
    });

  const [search, setSearch] = useState<string>('');
  const [visitedFields, setVisitedFields] = useState<string[]>([]);

  const selectedCount = useMemo(
    () => Object.values(selectedValues).reduce((acc, curr) => acc + curr.length, 0),
    [selectedValues]
  );

  const reset = useCallback(() => {
    setSelectedValues(
      selectedOptions?.reduce(
        (acc: SelectedValues, opt) => ({
          ...acc,
          [opt.field]: [...(acc[opt.field] ?? []), opt.key],
        }),
        {}
      ) || {}
    );
    setSelectedLevelValues(
      selectedOptions?.reduce((acc: SelectedLevelValues, opt) => {
        if (!opt.levelValue) return acc;
        return {
          ...acc,
          [opt.field]: {
            ...(acc[opt.field] ?? {}),
            [String(opt.key)]: opt.levelValue,
          },
        };
      }, {}) || {}
    );
    setSelectedAccreditationBodyValues(
      selectedOptions?.reduce((acc: SelectedAccreditationBodyValues, opt) => {
        if (opt.accreditationBodyValue == null) return acc;
        return {
          ...acc,
          [opt.field]: {
            ...(acc[opt.field] ?? {}),
            [String(opt.key)]: opt.accreditationBodyValue,
          },
        };
      }, {}) || {}
    );
  }, [selectedOptions]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      reset();
      setVisitedFields([]);
      setOpen(event.currentTarget);
    },
    [reset]
  );

  const handleClose = useCallback(() => {
    setSearch('');
    setActiveOption(null);
    setVisitedFields([]);
    setOpen(null);
    if (activeOption) {
      options[activeOption]?.onSearch?.(null);
    }
  }, [activeOption, options]);

  const onItemClicked = useCallback(
    (key: FilterDropdownKey) => {
      if (activeOption) {
        const isSelected = isFilterKeySelected(selectedValues[activeOption], key);

        setSelectedValues((prev) => {
          const currentValues = prev[activeOption] || [];
          return {
            ...prev,
            [activeOption]: toggleFilterSelection(currentValues, key),
          };
        });

        if (isSelected) {
          setSelectedLevelValues((previousLevels) => {
            const nextFieldLevels = { ...(previousLevels[activeOption] ?? {}) };
            delete nextFieldLevels[String(key)];

            return { ...previousLevels, [activeOption]: nextFieldLevels };
          });
          setSelectedAccreditationBodyValues((previousBodies) => {
            const nextFieldBodies = { ...(previousBodies[activeOption] ?? {}) };
            delete nextFieldBodies[String(key)];

            return { ...previousBodies, [activeOption]: nextFieldBodies };
          });
        }
      }
    },
    [activeOption, selectedValues]
  );

  const onLevelChanged = useCallback(
    (key: FilterDropdownKey, level: AuthorizationLevel) => {
      if (!activeOption) return;

      setSelectedLevelValues((prev) => ({
        ...prev,
        [activeOption]: {
          ...(prev[activeOption] ?? {}),
          [String(key)]: level,
        },
      }));
    },
    [activeOption]
  );

  const onAccreditationBodyChanged = useCallback(
    (key: FilterDropdownKey, accreditationBodyId: number) => {
      if (!activeOption) return;

      setSelectedAccreditationBodyValues((prev) => ({
        ...prev,
        [activeOption]: {
          ...(prev[activeOption] ?? {}),
          [String(key)]: accreditationBodyId,
        },
      }));
    },
    [activeOption]
  );

  const filteredOptions: SelectedFilterOption[] = useMemo(() => {
    if (!activeOption) return [];

    const activeOptionConfig = options[activeOption];

    return (
      activeOptionConfig?.children
        ?.filter((child) => !child.onClick)
        ?.map((child) => ({
          ...child,
          isSelected: Boolean(
            activeOption &&
              child?.key != null &&
              isFilterKeySelected(selectedValues[activeOption], child.key)
          ),
        })) ?? []
    );
  }, [options, selectedValues, activeOption]);

  const selectableVisibleKeys = useMemo(
    () =>
      filteredOptions
        .map((option) => option.key)
        .filter((key): key is string | number => key != null),
    [filteredOptions]
  );

  const activeFieldValues = activeOption ? selectedValues[activeOption] ?? [] : [];

  const allVisibleSelected = useMemo(
    () => areAllFilterKeysSelected(activeFieldValues, selectableVisibleKeys),
    [activeFieldValues, selectableVisibleKeys]
  );

  const someVisibleSelected = useMemo(
    () =>
      areSomeFilterKeysSelected(activeFieldValues, selectableVisibleKeys) && !allVisibleSelected,
    [activeFieldValues, selectableVisibleKeys, allVisibleSelected]
  );

  const onToggleSelectAllVisible = useCallback(() => {
    if (!activeOption || selectableVisibleKeys.length === 0) return;

    if (allVisibleSelected) {
      setSelectedValues((prev) => ({
        ...prev,
        [activeOption]: removeFilterSelections(prev[activeOption] ?? [], selectableVisibleKeys),
      }));
      setSelectedLevelValues((previousLevels) => {
        const nextFieldLevels = { ...(previousLevels[activeOption] ?? {}) };
        selectableVisibleKeys.forEach((key) => {
          delete nextFieldLevels[String(key)];
        });
        return { ...previousLevels, [activeOption]: nextFieldLevels };
      });
      setSelectedAccreditationBodyValues((previousBodies) => {
        const nextFieldBodies = { ...(previousBodies[activeOption] ?? {}) };
        selectableVisibleKeys.forEach((key) => {
          delete nextFieldBodies[String(key)];
        });
        return { ...previousBodies, [activeOption]: nextFieldBodies };
      });
      return;
    }

    setSelectedValues((prev) => ({
      ...prev,
      [activeOption]: addFilterSelections(prev[activeOption] ?? [], selectableVisibleKeys),
    }));
  }, [activeOption, allVisibleSelected, selectableVisibleKeys]);

  const onClearAllInField = useCallback(() => {
    if (!activeOption) return;

    setSelectedValues((prev) => ({
      ...prev,
      [activeOption]: [],
    }));
    setSelectedLevelValues((prev) => ({
      ...prev,
      [activeOption]: {},
    }));
    setSelectedAccreditationBodyValues((prev) => ({
      ...prev,
      [activeOption]: {},
    }));
  }, [activeOption]);

  const selectedOptionsForActiveField = useMemo(() => {
    if (!activeOption) return [] as SelectedOptions[];

    const keys = selectedValues[activeOption] ?? [];
    const children = options[activeOption]?.children ?? [];
    const childByKey = new Map(children.map((child) => [String(child.key), child]));
    const previousForField = (selectedOptions ?? []).filter((opt) => opt.field === activeOption);

    return keys
      .map((key) => {
        const child = childByKey.get(String(key));
        const previous = previousForField.find((opt) => opt.key === key);
        const accreditationBodyValue =
          selectedAccreditationBodyValues[activeOption]?.[String(key)] ??
          previous?.accreditationBodyValue;
        const accreditationBodyLabel =
          options[activeOption]?.accreditationBodyOptions?.find(
            (option) => String(option.key) === String(accreditationBodyValue)
          )?.label ?? previous?.accreditationBodyLabel;

        return {
          field: activeOption,
          key,
          label: child?.label ?? previous?.label ?? String(key),
          levelValue:
            selectedLevelValues[activeOption]?.[String(key)] ?? previous?.levelValue,
          accreditationBodyValue,
          accreditationBodyLabel,
        } satisfies SelectedOptions;
      })
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [
    activeOption,
    options,
    selectedOptions,
    selectedValues,
    selectedLevelValues,
    selectedAccreditationBodyValues,
  ]);

  const onApply = useCallback(() => {
    const mappedOptions: SelectedOptions[] = [];

    for (const field of Object.keys(selectedValues)) {
      const keys = selectedValues[field] ?? [];
      const children = options[field]?.children ?? [];
      const childByKey = new Map(children.map((child) => [String(child.key), child]));
      const previousForField = (selectedOptions ?? []).filter((opt) => opt.field === field);

      for (const key of keys) {
        const child = childByKey.get(String(key));
        const previous = previousForField.find((opt) => opt.key === key);
        const levelValue = selectedLevelValues[field]?.[String(key)];
        const accreditationBodyValue = selectedAccreditationBodyValues[field]?.[String(key)];
        const accreditationBodyLabel =
          options[field]?.accreditationBodyOptions?.find(
            (option) => String(option.key) === String(accreditationBodyValue)
          )?.label ?? previous?.accreditationBodyLabel;

        if (child) {
          mappedOptions.push({
            field,
            key: child.key,
            label: child.label,
            levelValue,
            accreditationBodyValue,
            accreditationBodyLabel,
          });
        } else if (previous) {
          mappedOptions.push({
            ...previous,
            levelValue: levelValue ?? previous.levelValue,
            accreditationBodyValue: accreditationBodyValue ?? previous.accreditationBodyValue,
            accreditationBodyLabel,
          });
        } else {
          mappedOptions.push({
            field,
            key,
            label: String(key),
            levelValue,
            accreditationBodyValue,
            accreditationBodyLabel,
          });
        }
      }
    }

    onOptionsChanged(mappedOptions, visitedFields);
    handleClose();
  }, [
    selectedValues,
    selectedLevelValues,
    selectedAccreditationBodyValues,
    options,
    selectedOptions,
    onOptionsChanged,
    handleClose,
    visitedFields,
  ]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      if (activeOption) {
        options[activeOption]?.onSearch?.(e.target.value);
      }
    },
    [activeOption, options]
  );

  const handleSetActiveOption = useCallback((key: string | null) => {
    if (key) {
      setVisitedFields((previous) => (previous.includes(key) ? previous : [...previous, key]));
    }
    setActiveOption(key);
  }, []);

  useEffect(() => {
    // Parent re-renders (e.g. schedule data loading) must not wipe in-progress multi-select.
    if (isOpen) return;
    reset();
  }, [selectedOptions, reset, isOpen]);

  const optionKeys = useMemo(() => Object.keys(options), [options]);

  const isMenuOpen = Boolean(isOpen);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disableRipple
        onClick={handleOpen}
        sx={{
          height: buttonDimensions.height,
          width: buttonDimensions.width || 82,
          // Remove MUI transition "fade" so hover/click state changes instantly.
          transition: 'none !important',
          // Remove "clicked/pressed" background/ripple effects; keep hover as-is.
          '&:active': { backgroundColor: isMenuOpen ? 'action.hover' : 'transparent' },
          // While the popover is open, keep the hover fill.
          backgroundColor: isMenuOpen ? 'action.hover' : undefined,
          // While the popover is open, keep outline ("stroke") in sync with text.
          borderColor: isMenuOpen ? 'currentColor' : undefined,
        }}
      >
        <Iconify icon="mynaui:filter-solid" /> {buttonLabel}
      </Button>
      <Menu
        id="filter-menu"
        anchorEl={isOpen}
        onClose={handleClose}
        open={isMenuOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={0}
        keepMounted={false}
        PaperProps={{
          sx: (theme) => ({
            // Remove outer popup stroke; keep subtle background + shadow (theme-aware for dark mode).
            border: 'none',
            boxShadow: `inset 0 0 0 1px ${varAlpha(theme.vars.palette.grey['500Channel'], theme.palette.mode === 'dark' ? 0.24 : 0.32)}, 0 4px 10px ${varAlpha(theme.vars.palette.common.blackChannel, theme.palette.mode === 'dark' ? 0.4 : 0.12)}`,
            // Neutralize the theme "dropdown" background blur/overlays so it matches the subtle reference look.
            backgroundImage: 'none !important',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            // Remove dropdown padding so the divider line reaches the edges.
            p: 0,
            // Create a small visual gap under the trigger button.
            mt: '4px',
          }),
        }}
      >
        <Box
          sx={{
            width:
              activeOption === COMPETENCY_FILTER_FIELD &&
              (options[activeOption]?.accreditationBodyOptions?.length ?? 0) > 0
                ? 420
                : 300,
          }}
        >
          {activeOption ? (
            <Box>
              {options[activeOption]?.onSearch && (
                <MenuItem
                  key="search"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <TextField
                    variant="standard"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder={t('search.placeholder')}
                    InputProps={{
                      startAdornment: <Iconify icon="material-symbols:search-rounded" />,
                    }}
                    fullWidth
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      '&:hover': {
                        bgColor: 'transparent',
                      },
                    }}
                  />
                </MenuItem>
              )}
              <MenuItem
                key="back"
                onClick={() => {
                  setSearch('');
                  options[activeOption]?.onSearch?.(null);
                  handleSetActiveOption(null);
                }}
              >
                <Iconify icon="solar:arrow-left-linear" sx={{ pr: 0.5 }} />
                {options[activeOption].label}
              </MenuItem>
              {activeOption === COMPETENCY_FILTER_FIELD && selectedOptionsForActiveField.length > 0 && (
                <Box
                  sx={(theme) => ({
                    mx: 1,
                    mb: 0.5,
                    pb: 0.5,
                    borderBottom: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.24)}`,
                  })}
                >
                  {selectedOptionsForActiveField.map((option) => (
                    <SelectedCompetencyDetailRow
                      key={`${option.field}:${String(option.key)}`}
                      label={option.label}
                      levelValue={option.levelValue}
                      onLevelChange={(level) => onLevelChanged(option.key, level)}
                      accreditationBodyValue={option.accreditationBodyValue}
                      onAccreditationBodyChange={(accreditationBodyId) =>
                        onAccreditationBodyChanged(option.key, accreditationBodyId)
                      }
                      accreditationBodyOptions={options[activeOption]?.accreditationBodyOptions}
                      accreditationBodyPlaceholder={
                        options[activeOption]?.accreditationBodyPlaceholder
                      }
                    />
                  ))}
                </Box>
              )}
              {enableFieldBulkActions && selectableVisibleKeys.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <FormControlLabel
                    onClick={(event) => event.stopPropagation()}
                    control={
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={onToggleSelectAllVisible}
                        disableRipple
                        sx={{
                          transition: 'none !important',
                          '& .MuiSvgIcon-root': { transition: 'none !important' },
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#00D5D4' },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">{t('filters.buttons.selectAll')}</Typography>
                    }
                    sx={{ m: 0, flex: 1, minWidth: 0 }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    onClick={onClearAllInField}
                    sx={{ minWidth: 0, px: 0.5, flexShrink: 0 }}
                  >
                    {t('filters.buttons.clearAll')}
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  p: 0,
                  height: Math.min(50 * filteredOptions.length, 400),
                  maxHeight: 'calc(100vh - 300px)',
                }}
              >
                <Virtuoso
                  style={{ height: '100%' }}
                  data={filteredOptions}
                  overscan={10}
                  endReached={() => {
                    options[activeOption]?.loadMore?.();
                  }}
                  itemContent={(index, child) => (
                    <FilterMenuItem
                      key={child?.key}
                      option={child}
                      isSelected={child?.isSelected}
                      onClick={() => {
                        if (child?.onClick) {
                          child?.onClick?.();
                          handleClose();
                        } else {
                          onItemClicked(child?.key);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          ) : (
            optionKeys.map((key) => (
              <MenuItem
                key={key}
                onClick={() => {
                  handleSetActiveOption(key);
                  if (options[key]?.onClick) {
                    options[key].onClick?.();
                    handleClose();
                  }
                }}
                sx={insetMenuItemSx}
              >
                {options[key].label}
              </MenuItem>
            ))
          )}
          {activeOption && (
            <MenuItem
              key="actions"
              disableRipple
              sx={(theme) => ({
                p: 0,
                borderTop: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
                mt: 0,
                borderRadius: 0,
              })}
            >
              <Box justifyContent="flex-end" sx={{ display: 'flex', gap: 2, width: '100%', p: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    reset();
                    handleClose();
                  }}
                  size="small"
                >
                  {cancelLabel}
                </Button>
                <Button
                  variant="contained"
                  onClick={onApply}
                  size="small"
                  disabled={selectedCount === 0}
                >
                  {applyLabel}
                </Button>
              </Box>
            </MenuItem>
          )}
        </Box>
      </Menu>
    </>
  );
};
